package com.steamstreet.vegasful.browser.components

import kotlin.math.floor

/** The time in Las Vegas, as a month, a day of the month and minutes after midnight. */
class VegasTime(val month: Int, val day: Int, val minutes: Int)

private val vegasClock: dynamic = js(
    "new Intl.DateTimeFormat('en-US', {timeZone: 'America/Los_Angeles', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit', hourCycle: 'h23'})"
)

/** The time in Las Vegas now, by the Las Vegas clock rather than the visitor's. */
fun vegasNow(): VegasTime {
    val parts = mutableMapOf<String, String>()
    val list = vegasClock.formatToParts(js("new Date()"))
    for (i in 0 until (list.length as Int)) {
        parts[list[i].type as String] = list[i].value as String
    }
    val hour = (parts["hour"]?.toIntOrNull() ?: 0) % 24
    return VegasTime(
        (parts["month"]?.toIntOrNull() ?: 1) - 1,
        parts["day"]?.toIntOrNull() ?: 15,
        hour * 60 + (parts["minute"]?.toIntOrNull() ?: 0)
    )
}

/**
 * Approximate Las Vegas sunrise and sunset on the 15th of each month, in local minutes after
 * midnight. Near enough to colour a sky; nothing here is shown as a time.
 */
private val SUNRISE = intArrayOf(411, 395, 420, 380, 340, 321, 331, 355, 379, 403, 374, 402)
private val SUNSET = intArrayOf(1019, 1051, 1137, 1161, 1184, 1200, 1199, 1176, 1134, 1092, 997, 989)

private fun sunTimes(time: VegasTime): Pair<Int, Int> {
    val position = time.month + (time.day - 15) / 30.0
    val first = floor(position).toInt()
    val fraction = position - first
    val a = ((first % 12) + 12) % 12
    val b = (a + 1) % 12
    fun between(values: IntArray) = (values[a] + (values[b] - values[a]) * fraction).toInt()
    return between(SUNRISE) to between(SUNSET)
}

/**
 * The part of the day [time] falls in: `late`, `dawn`, `day`, `golden`, `sunset` or `night`. Each
 * is also the name of one of [NeonSky]'s skies.
 */
fun phaseOf(time: VegasTime): String {
    val (rise, set) = sunTimes(time)
    val m = time.minutes
    return when {
        m < rise - 45 -> "late"
        m < rise + 60 -> "dawn"
        m < set - 100 -> "day"
        m < set - 20 -> "golden"
        m < set + 25 -> "sunset"
        else -> "night"
    }
}

/** Whether it is late enough in Las Vegas to call the rest of the day "tonight". */
fun isTonight(time: VegasTime) = phaseOf(time) in setOf("late", "night", "sunset", "golden")

private val vegasDate: dynamic = js(
    "new Intl.DateTimeFormat('en-CA', {timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit'})"
)

/** Today's date in Las Vegas. The `en-CA` locale formats a date as ISO-8601, which this parses. */
fun vegasToday(): kotlinx.datetime.LocalDate =
    kotlinx.datetime.LocalDate.parse(vegasDate.format(js("new Date()")) as String)
